/**
 * Bounce Coupon Manager
 * Handles unique coupon code generation, storage, and tracking
 */

class BounceCouponManager {
  constructor() {
    this.cookieName = 'bounce_discount_code';
    this.cookieExpireDays = 30;

    // UPDATE THIS with your Cloudflare Worker URL
    this.apiBaseUrl = 'https://pe-exit-intent-discount.mybeta.workers.dev';
    // If you are proxying via Shopify or Pages, you might use a relative path


    this.codeElement = document.getElementById('bounceCouponCode');
    this.copyButton = document.getElementById('bounceCopyCoupon');
    this.errorElement = document.getElementById('bounceCouponError');
    this.previewControls = document.getElementById('bouncePreviewControls');
    this.refreshButton = document.getElementById('bounceRefreshCoupon');
    this.currentCode = null;
    this.isPreviewMode = this.checkPreviewMode();

    if (this.codeElement) {
      this.init();
    }
  }

  checkPreviewMode() {
    // Preview mode requires both backend setting enabled AND URL parameter
    const previewEnabled = window.bouncePreviewEnabled || false;

    if (!previewEnabled) {
      return false;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const previewParam = urlParams.get('preview');

    if (previewParam === 'true') {
      console.log('🔍 Preview mode activated for coupon manager');
      return true;
    }

    return false;
  }

  init() {
    // Prevent execution in Theme Editor to allow page editing
    if (window.Shopify && window.Shopify.designMode) {
      console.log('Theme editor mode detected - skipping coupon manager');
      // Show placeholder in theme editor
      if (this.codeElement) {
        this.codeElement.textContent = 'PREVIEW20';
        this.codeElement.classList.remove('loading');
      }
      return;
    }

    // Prevent execution if bounce urgency section was disabled
    if (window.bounceUrgencyDisabled) {
      console.log('Bounce urgency disabled - skipping coupon manager');
      return;
    }

    // Check if user already has a code in localStorage
    let existingCode = null;
    let expiryTime = null;

    // Use localStorage as primary and only storage (cookies removed)
    try {
      const storedData = localStorage.getItem('bounce_coupon_data');
      if (storedData) {
        const data = JSON.parse(storedData);
        existingCode = data.code;
        expiryTime = data.expiryTime;
      }
    } catch (e) {
      console.warn('localStorage not available, will generate new code');
    }

    if (existingCode && expiryTime) {
      // Check if code is still valid
      if (Date.now() < expiryTime) {
        this.displayCode(existingCode);
      } else {
        // Code expired - show expired state
        this.displayCode(existingCode);
        this.showExpiredState();
      }
    } else if (existingCode) {
      // Have code but no expiry - display it
      this.displayCode(existingCode);
    } else {
      // No existing code - generate new one
      this.generateNewCode();
    }

    // Setup copy button
    this.setupCopyButton();

    // Setup refresh button if in preview mode (but keep hidden initially)
    if (this.isPreviewMode && this.previewControls) {
      this.setupRefreshButton();
      console.log('🔍 Preview mode: Refresh button ready');

      // Only show if coupon is already expired
      if (existingCode && expiryTime && Date.now() >= expiryTime) {
        this.previewControls.style.display = 'block';
        console.log('🔍 Preview controls shown (coupon expired)');
      }
    }
  }

  async generateNewCode() {
    try {
      // Try to generate via API
      const code = await this.generateViaAPI();

      if (code) {
        this.saveCode(code);
        this.displayCode(code);
        this.trackCouponGenerated(code);
      } else {
        // No code generated - show error instead of fake code
        this.showError('Gutscheincode konnte nicht generiert werden. Bitte lade die Seite neu oder kontaktiere den Support.');
        this.displayLoadingState();
        this.triggerTimerError(); // Tell timer to show 00:00:00
      }
    } catch (error) {
      console.error('Coupon generation failed:', error);
      // Show error instead of generating fake code
      this.showError('Ein Fehler ist aufgetreten. Bitte versuche es später erneut oder kontaktiere unseren Support.');
      this.displayLoadingState();
      this.triggerTimerError(); // Tell timer to show 00:00:00
    }
  }

  async generateViaAPI() {
    if (!this.apiBaseUrl) return null;

    try {
      const fingerprint = this.generateFingerprint();

      // Get cookie values to send as custom headers (cross-origin workaround)
      const bouncePageAccess = this.getCookie('bounce_page_access');
      const csrfToken = this.getCookie('bounce_csrf_token');

      // Get shop configuration for offer duration and discount amount
      const offerExpiryMinutes = this.getOfferExpiryMinutes();
      const discountAmount = this.getDiscountAmount();

      // Get expiry from localStorage if available
      let bounceOfferExpiry = '';
      try {
        const couponData = localStorage.getItem('bounce_coupon_data');
        if (couponData) {
          const data = JSON.parse(couponData);
          bounceOfferExpiry = data.expiryTime ? data.expiryTime.toString() : '';
        }
      } catch (e) {
        // No expiry available
      }

      const payload = {
        fingerprint: fingerprint
      };

      // Debug logging
      // console.log('Initiating coupon generation with fingerprint:', fingerprint);
      // console.log('Sending headers:', { bouncePageAccess, bounceOfferExpiry, offerExpiryMinutes, discountAmount });
      // console.log('CSRF token:', csrfToken ? 'Present' : 'Missing');

      // Direct POST to Worker with custom headers including discount amount
      const response = await fetch(this.apiBaseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Bounce-Page-Access': bouncePageAccess || '',
          'X-Bounce-Offer-Expiry': bounceOfferExpiry || '',
          'X-Bounce-Offer-Expiry-Minutes': offerExpiryMinutes.toString(),
          'X-Bounce-Discount-Amount': discountAmount.toString(),
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify(payload)
      });

      // Handle specific error cases
      if (response.status === 401) {
        this.showError('Unauthorized access. Please refresh the page and try again.');
        return null;
      }

      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        this.showError(errorData.error || 'This offer has expired or is no longer available.');
        return null;
      }

      if (response.status === 429) {
        this.showError('Too many requests. Please try again in an hour.');
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Worker error:', errorData);
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.code && data.expiryTime) {
         // Debug logging
         // console.log(`Coupon generated successfully. Expires at: ${new Date(data.expiryTime).toISOString()}`);

         // Save to localStorage (cookies removed - localStorage is now primary storage)
         try {
           localStorage.setItem('bounce_coupon_data', JSON.stringify({
             code: data.code,
             expiryTime: data.expiryTime,
             generatedAt: Date.now()
           }));
         } catch (e) {
           console.warn('localStorage not available');
         }

         // Dispatch event to start timer
         const timerEvent = new CustomEvent('couponGenerated', {
           detail: {
             code: data.code,
             expiryTime: data.expiryTime
           }
         });
         document.dispatchEvent(timerEvent);

         return data.code;
      }
      return null;

    } catch (error) {
      console.warn('API generation failed:', error);
      return null;
    }
  }

  getOfferExpiryMinutes() {
    // Read from Shopify theme settings
    // Settings are exposed via window.themeSettings or we can read from a data attribute
    try {
      // Try to read from window.themeSettings if available
      if (window.themeSettings && window.themeSettings.bounce_offer_duration) {
        const hours = parseInt(window.themeSettings.bounce_offer_duration);
        if (!isNaN(hours) && hours > 0 && hours <= 24) {
          return hours * 60; // Convert hours to minutes
        }
      }

      // Fallback: try to read from data attribute on body or root element
      const rootElement = document.documentElement || document.body;
      const durationHours = rootElement.dataset.bounceOfferDuration;
      if (durationHours) {
        const hours = parseInt(durationHours);
        if (!isNaN(hours) && hours > 0 && hours <= 24) {
          return hours * 60; // Convert hours to minutes
        }
      }
    } catch (error) {
      console.warn('Could not read bounce_offer_duration setting:', error);
    }

    // Default fallback: 8 hours (480 minutes)
    return 480;
  }

  getDiscountAmount() {
    // Read discount amount from Shopify theme settings
    try {
      // Try to read from window.themeSettings if available
      if (window.themeSettings && window.themeSettings.bounce_discount_amount) {
        const amount = parseInt(window.themeSettings.bounce_discount_amount);
        if (!isNaN(amount) && amount >= 5 && amount <= 100) {
          return amount;
        }
      }

      // Fallback: try to read from data attribute on body or root element
      const rootElement = document.documentElement || document.body;
      const discountAmount = rootElement.dataset.bounceDiscountAmount;
      if (discountAmount) {
        const amount = parseInt(discountAmount);
        if (!isNaN(amount) && amount >= 5 && amount <= 100) {
          return amount;
        }
      }
    } catch (error) {
      console.warn('Could not read bounce_discount_amount setting:', error);
    }

    // Default fallback: 20€
    return 20;
  }

  generateFingerprint() {
    // Simple fingerprint based on browser characteristics
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      Date.now() // Add timestamp for uniqueness
    ].join('|');

    // Create hash
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    // Convert to base36 and ensure minimum 8 characters
    let result = Math.abs(hash).toString(36).toUpperCase();

    // Pad with additional hash if too short
    while (result.length < 8) {
      result += Math.random().toString(36).substring(2, 3).toUpperCase();
    }

    return result.substring(0, 12); // Return 12 chars for better uniqueness
  }

  saveCode(code) {
    this.currentCode = code;
    // Cookie storage removed - localStorage is now primary storage

    // Save to localStorage only
    try {
      localStorage.setItem(this.cookieName, code);
      localStorage.setItem(`${this.cookieName}_date`, new Date().toISOString());
    } catch (e) {
      console.warn('localStorage not available');
    }
  }

  displayCode(code) {
    this.currentCode = code;
    if (this.codeElement) {
      this.codeElement.textContent = code;
      this.codeElement.classList.remove('loading');
    }
    if (this.copyButton) {
      this.copyButton.disabled = false;
    }
  }

  displayLoadingState() {
    if (this.codeElement) {
      this.codeElement.textContent = '---';
      this.codeElement.classList.add('loading');
    }
    if (this.copyButton) {
      this.copyButton.disabled = true;
      const buttonText = this.copyButton.querySelector('.bounce-coupon__copy-text');
      if (buttonText) {
        buttonText.textContent = 'Nicht verfügbar';
      }
    }
  }

  setupCopyButton() {
    if (!this.copyButton) return;

    this.copyButton.addEventListener('click', () => {
      this.copyToClipboard();
    });
  }

  setupRefreshButton() {
    if (!this.refreshButton) return;

    this.refreshButton.addEventListener('click', async () => {
      await this.requestNewCoupon();
    });
  }

  async requestNewCoupon() {
    if (!this.isPreviewMode) {
      console.warn('Request new coupon is only available in preview mode');
      return;
    }

    // Disable button during generation
    if (this.refreshButton) {
      this.refreshButton.disabled = true;
      this.refreshButton.querySelector('span').textContent = 'Generating...';
    }

    // Clear existing coupon data
    try {
      localStorage.removeItem('bounce_coupon_data');
      localStorage.removeItem(this.cookieName);
      localStorage.removeItem(`${this.cookieName}_date`);
      console.log('🔍 Preview mode: Cleared existing coupon data');
    } catch (e) {
      console.warn('Could not clear localStorage');
    }

    // Clear error message
    if (this.errorElement) {
      this.errorElement.style.display = 'none';
      this.errorElement.textContent = '';
    }

    // Remove all expired classes
    this.removeExpiredClasses();

    // Clear expired status message
    const statusMessage = document.querySelector('.bounce-coupon__status');
    if (statusMessage) {
      statusMessage.style.display = 'none';
      statusMessage.textContent = '';
      statusMessage.classList.remove('expired');
    }

    // Reset timer display
    const timerDisplay = document.querySelector('.bounce-timer__display');
    if (timerDisplay) {
      timerDisplay.textContent = '--:--:--';
      timerDisplay.classList.remove('expired');
    }

    // Re-enable copy button
    if (this.copyButton) {
      this.copyButton.disabled = false;
      const buttonText = this.copyButton.querySelector('.bounce-coupon__copy-text');
      if (buttonText) {
        buttonText.textContent = 'Code kopieren';
      }
    }

    // Show loading state
    this.displayLoadingState();

    // Generate new CSRF token and grant bounce page access
    await this.regenerateAccess();

    // Generate new code
    await this.generateNewCode();

    // Re-enable button
    if (this.refreshButton) {
      this.refreshButton.disabled = false;
      this.refreshButton.querySelector('span').textContent = 'Request New Coupon';
    }

    // Hide preview controls after generating new coupon (will show again when it expires)
    if (this.previewControls) {
      this.previewControls.style.display = 'none';
      console.log('🔍 Preview controls hidden (new coupon active)');
    }

    console.log('🔍 Preview mode: New coupon and CSRF token generated');
  }

  removeExpiredClasses() {
    // Remove expired class from coupon section
    const couponSection = document.querySelector('.bounce-coupon');
    if (couponSection) {
      couponSection.classList.remove('expired');
    }

    // Remove expired class from timer display
    const timerDisplay = document.querySelector('.bounce-timer__display');
    if (timerDisplay) {
      timerDisplay.classList.remove('expired');
    }

    // Remove expired class from code element
    if (this.codeElement) {
      this.codeElement.classList.remove('expired');
    }

    console.log('🔍 Preview mode: Removed expired classes');
  }

  async regenerateAccess() {
    // Generate new CSRF token
    const csrfToken = await this.generateCSRFToken();

    // Set new bounce_page_access cookie with 1-week expiry
    const now = new Date();
    const accessExpiry = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000)); // 1 week
    const expiryTimestamp = accessExpiry.getTime();

    document.cookie = `bounce_page_access=${expiryTimestamp};expires=${accessExpiry.toUTCString()};path=/;SameSite=Lax`;

    // Store CSRF token in cookie (10 min expiry)
    const csrfExpiry = new Date(now.getTime() + (10 * 60 * 1000)); // 10 minutes
    document.cookie = `bounce_csrf_token=${csrfToken};expires=${csrfExpiry.toUTCString()};path=/;SameSite=Lax`;

    // Register CSRF token with Worker
    await this.registerCSRFToken(csrfToken);

    console.log('🔍 Preview mode: New CSRF token and access granted');
  }

  async generateCSRFToken() {
    // Generate cryptographically secure random token
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  async registerCSRFToken(token) {
    // Register token with Worker (it will store in KV with TTL)
    try {
      const response = await fetch(`${this.apiBaseUrl}/register-csrf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          timestamp: Date.now()
        })
      });

      if (response.ok) {
        console.log('🔍 Preview mode: CSRF token registered successfully');
      } else {
        console.warn('Failed to register CSRF token');
      }
    } catch (error) {
      console.warn('Could not register CSRF token:', error);
    }
  }

  async copyToClipboard() {
    if (!this.currentCode) return;

    try {
      // Modern clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(this.currentCode);
        this.showCopySuccess();
        this.trackCouponCopied(this.currentCode);
      } else {
        // Fallback for older browsers
        this.fallbackCopyToClipboard(this.currentCode);
      }
    } catch (error) {
      console.error('Copy failed:', error);
      this.fallbackCopyToClipboard(this.currentCode);
    }
  }

  fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showCopySuccess();
      this.trackCouponCopied(text);
    } catch (error) {
      this.showError('Kopieren fehlgeschlagen. Bitte manuell markieren und kopieren.');
    }

    document.body.removeChild(textArea);
  }

  showCopySuccess() {
    if (!this.copyButton) return;

    const originalText = this.copyButton.querySelector('.bounce-coupon__copy-text').textContent;
    this.copyButton.querySelector('.bounce-coupon__copy-text').textContent = 'Kopiert!';
    this.copyButton.classList.add('copied');

    setTimeout(() => {
      this.copyButton.querySelector('.bounce-coupon__copy-text').textContent = originalText;
      this.copyButton.classList.remove('copied');
    }, 2000);
  }

  showError(message) {
    if (this.errorElement) {
      this.errorElement.textContent = message;
      this.errorElement.style.display = 'block';
    }
  }

  triggerTimerError() {
    // Dispatch custom event to notify timer component
    const event = new CustomEvent('couponGenerationFailed', {
      detail: { message: 'Coupon generation failed or expired' }
    });
    document.dispatchEvent(event);
  }

  showExpiredState() {
    // Show expired message
    const statusMessage = document.querySelector('.bounce-coupon__status');
    if (statusMessage) {
      statusMessage.textContent = '⚠️ Dein Gutschein ist abgelaufen';
      statusMessage.classList.add('expired');
      statusMessage.style.display = 'block';
    }

    // Disable copy button
    if (this.copyButton) {
      this.copyButton.disabled = true;
      const buttonText = this.copyButton.querySelector('.bounce-coupon__copy-text');
      if (buttonText) {
        buttonText.textContent = 'Abgelaufen';
      }
    }

    // Add expired styling to coupon section
    const couponSection = document.querySelector('.bounce-coupon');
    if (couponSection) {
      couponSection.classList.add('expired');
    }

    // Ensure preview controls stay visible in preview mode
    if (this.isPreviewMode && this.previewControls) {
      this.previewControls.style.display = 'block';
      console.log('🔍 Preview controls kept visible in expired state');
    }
  }

  // Cookie management
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Klaviyo tracking
  trackCouponGenerated(code) {
    if (typeof _learnq === 'undefined') return;

    const eventData = {
      DiscountCode: code,
      DiscountValue: this.getDiscountAmount(),
      DiscountType: 'fixed_amount',
      Currency: 'EUR',
      GeneratedAt: new Date().toISOString(),
      PageURL: window.location.href
    };

    _learnq.push(['track', 'Generated Bounce Discount Code', eventData]);

    // Update profile
    _learnq.push(['identify', {
      BounceDiscountCode: code,
      HasBounceDiscount: true,
      BounceDiscountGeneratedDate: new Date().toISOString()
    }]);

    // console.log('Klaviyo: Discount code tracked', code);
  }

  trackCouponCopied(code) {
    if (typeof _learnq === 'undefined') return;

    _learnq.push(['track', 'Copied Bounce Discount Code', {
      DiscountCode: code,
      CopiedAt: new Date().toISOString()
    }]);

    // console.log('Klaviyo: Coupon copy tracked', code);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BounceCouponManager();
  });
} else {
  new BounceCouponManager();
}
